/**
 * FAQ Data - Edit this file to update FAQ content
 * Structure: Array of categories, each with id, title, and items (q/a pairs)
 */

export const faqCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        q: 'What is Flippex?',
        a: 'Flippex is a real estate investment platform that enables fractional ownership of curated properties. We connect investors with quality real estate projects, allowing you to invest from as little as €100 and earn returns from rental income and property appreciation.'
      },
      {
        q: 'Who can invest on Flippex?',
        a: 'Flippex is open to investors across Europe who want to access real estate investment opportunities. You need to complete identity verification (KYC) to start investing. Each project may have specific eligibility requirements based on your jurisdiction.'
      },
      {
        q: 'How is Flippex different from buying property directly?',
        a: 'With Flippex, you can invest in real estate starting from €100 without the hassle of mortgages, property management, or tenant issues. You own a fraction of professionally managed properties and receive your share of rental income and appreciation.'
      },
      {
        q: 'Is Flippex a bank or financial advisor?',
        a: 'No. Flippex does not provide personalized investment advice. Information on the platform is for informational purposes and you should make your own decision or consult a professional advisor.'
      }
    ]
  },
  {
    id: 'for-investors',
    title: 'For Investors',
    items: [
      {
        q: 'What types of projects are available?',
        a: 'Flippex offers curated real estate projects across different asset types including residential, student housing, and commercial properties. Each project is vetted for investment potential and includes detailed documentation about the property, strategy, and projected returns.'
      },
      {
        q: 'How does fractional investment work?',
        a: 'Fractional investment allows you to own a portion of a real estate asset without purchasing the entire property. Your investment is represented digitally, and you receive proportional returns from rental income and property appreciation.'
      },
      {
        q: 'What returns can I expect?',
        a: 'Returns vary by project and come from two sources: regular income (from rent) and capital appreciation (from property value increases). Each project page shows projected returns, but these are estimates, not guarantees. Real estate investments involve risk.'
      },
      {
        q: 'How do I get started as an investor?',
        a: 'Create an account, complete identity verification (KYC), browse available projects, review project details and disclosures, then invest from €100. Your dashboard tracks all your investments and distributions.'
      },
      {
        q: 'Can I diversify across multiple projects?',
        a: 'Yes! One of the key benefits of Flippex is the ability to spread your investment across multiple properties and asset types, reducing risk while maintaining exposure to real estate returns.'
      }
    ]
  },
  {
    id: 'for-developers',
    title: 'For Developers',
    items: [
      {
        q: 'Can I list my project on Flippex?',
        a: 'Yes! Real estate developers and sponsors can submit their projects for consideration. Visit the For Developers page and complete the submission form. We\'ll review your project and get back to you within 48 hours.'
      },
      {
        q: 'What types of projects does Flippex accept?',
        a: 'We consider various real estate projects including residential developments, student housing, and commercial properties. Projects should have a clear investment thesis, professional management, and appropriate legal structure.'
      },
      {
        q: 'What does Flippex handle for developers?',
        a: 'We handle the entire fundraising process including investor onboarding, compliance, capital collection, and ongoing investor relations and reporting. You focus on developing great properties while we manage the investor side.'
      },
      {
        q: 'How long does the review process take?',
        a: 'After you submit your project, our team conducts due diligence and reviews the investment potential. We aim to provide initial feedback within 48 hours and complete the full review process within 2-3 weeks for qualified projects.'
      }
    ]
  },
  {
    id: 'returns-distributions',
    title: 'Returns & Distributions',
    items: [
      {
        q: 'When do distributions happen?',
        a: 'Distribution timing varies by project. Most projects distribute quarterly from rental income. Capital appreciation is typically realized when the property is sold or refinanced. Check each project page for specific distribution schedules.'
      },
      {
        q: 'Are returns guaranteed?',
        a: 'No. Real estate investments involve risk and returns can be higher or lower than projections, including the possibility of loss. Projections are estimates based on market analysis, not promises.'
      },
      {
        q: 'How can I track my investments?',
        a: 'Your dashboard shows portfolio performance, distributions received, and project updates. You\'ll receive notifications for important milestones, quarterly reports, and any significant project developments.'
      },
      {
        q: 'How are distributions paid?',
        a: 'Distributions are paid directly to your linked bank account. You can view your distribution history and upcoming payments in your investor dashboard.'
      }
    ]
  },
  {
    id: 'fees-structure',
    title: 'Fees & Structure',
    items: [
      {
        q: 'What fees does Flippex charge?',
        a: 'Flippex charges transparent fees that vary by project. All fees are clearly disclosed on each project page before you invest. There are no hidden charges or surprise costs.'
      },
      {
        q: 'Do I directly own the property?',
        a: 'Investors typically own an interest in a legal vehicle (e.g., SPV) that holds the property, rather than being on the property deed personally. This structure provides liability protection and enables fractional ownership. The exact structure is disclosed per project.'
      },
      {
        q: 'Is there a secondary market to sell my position?',
        a: 'The Flippex Marketplace (coming soon) will allow investors to trade their positions with other investors. Until then, investments should be considered long-term and held until the project exit.'
      },
      {
        q: 'How are taxes handled?',
        a: 'Tax treatment depends on your country of residence and the investment structure. Flippex provides annual tax documentation and transaction summaries. You are responsible for your own tax filings and should consult a tax advisor.'
      }
    ]
  },
  {
    id: 'verification-security',
    title: 'Security & Support',
    items: [
      {
        q: 'Do I need to complete identity verification (KYC)?',
        a: 'Yes, identity verification is required to invest. This process helps prevent fraud, ensures compliance with regulations, and protects all platform users. The verification process is quick and guided step-by-step.'
      },
      {
        q: 'How do you protect my data and account?',
        a: 'We use industry-standard security measures including secure authentication, encrypted data transmission, and strict access controls. Your personal and financial data is handled according to our Privacy Policy and GDPR requirements.'
      },
      {
        q: 'Is my investment protected?',
        a: 'Each investment is structured through regulated vehicles with proper legal documentation. While investments are not deposit-guaranteed, the legal structure protects your ownership rights in the underlying real estate assets.'
      },
      {
        q: 'How can I contact Flippex support?',
        a: 'Use the contact form on our Contact page or email support@flippex.com. We aim to respond within 24-48 hours on business days.'
      }
    ]
  }
];

/**
 * Get all FAQ items as a flat array (for search)
 */
export const getAllFaqItems = () => {
  return faqCategories.flatMap(cat => 
    cat.items.map(item => ({
      ...item,
      categoryId: cat.id,
      categoryTitle: cat.title
    }))
  );
};
